import affiliateApi from '@/Network/Requests/affiliateApi'
import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import type {
  AffiliatePackagesQuery,
  AffiliateScope
} from '@/Network/Types/Requests/AntecipeAffiliateRequests'
import type {
  AffiliateMeResponse,
  AffiliatePackageDetailResponse,
  AffiliatePackagesResponse,
  AffiliatePayoutResponse,
  AffiliateSummaryResponse,
  AntecipeAffiliateListItem
} from '@/Network/Types/Responses/AntecipeAffiliateResponses'

/** Mesmas rotas nos dois escopos (contrato B): o "ver como" só troca a base. */
export const affiliateBasePath = (scope: AffiliateScope): string =>
  scope.kind === 'self' ? '/affiliate' : `/admin/antecipe-affiliates/${scope.affiliateId}`

/** Só filtros preenchidos sobem; números viram texto; `q` aparado. */
export const buildAffiliatePackagesParams = (
  query: AffiliatePackagesQuery
): Record<string, string> => {
  const params: Record<string, string> = {}
  if (query.tab) params.tab = query.tab
  if (query.unitId) params.unitId = String(query.unitId)
  if (query.stage) params.stage = query.stage
  if (query.slaState) params.slaState = query.slaState
  const q = query.q?.trim()
  if (q) params.q = q
  if (query.page) params.page = String(query.page)
  if (query.pageSize) params.pageSize = String(query.pageSize)
  return params
}

export default class AntecipeAffiliateService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/affiliate'
  }

  private async read<R>(
    scope: AffiliateScope,
    path: string,
    params: Record<string, string> | undefined,
    defaultMessage: string
  ): Promise<R> {
    const url = `${affiliateBasePath(scope)}${path}`
    try {
      const { data } =
        scope.kind === 'self'
          ? await affiliateApi.get<R>(url, { params })
          : await request.get<R>(url, params)
      return data
    } catch (e) {
      throw this.handleError(e, defaultMessage)
    }
  }

  getMe(scope: AffiliateScope): Promise<AffiliateMeResponse> {
    return this.read(scope, '/me', undefined, 'Não foi possível carregar os dados do afiliado.')
  }

  getSummary(scope: AffiliateScope, month: string): Promise<AffiliateSummaryResponse> {
    return this.read(scope, '/summary', { month }, 'Não foi possível carregar a visão geral.')
  }

  listPackages(
    scope: AffiliateScope,
    query: AffiliatePackagesQuery
  ): Promise<AffiliatePackagesResponse> {
    return this.read(
      scope,
      '/packages',
      buildAffiliatePackagesParams(query),
      'Não foi possível carregar os pacotes.'
    )
  }

  getPackage(scope: AffiliateScope, id: number): Promise<AffiliatePackageDetailResponse> {
    return this.read(scope, `/packages/${id}`, undefined, 'Não foi possível carregar o pacote.')
  }

  getPayout(scope: AffiliateScope, month: string): Promise<AffiliatePayoutResponse> {
    return this.read(scope, '/payout', { month }, 'Não foi possível carregar o repasse.')
  }

  async listAffiliates(): Promise<AntecipeAffiliateListItem[]> {
    try {
      const { data } = await request.get<AntecipeAffiliateListItem[]>('/admin/antecipe-affiliates')
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os afiliados.')
    }
  }
}
